<template>
    <v-form autocomplete="off">
        <v-card>
            <v-toolbar dark color="primary">
                <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
                <slot name="header"></slot>
            </v-toolbar>
            <v-container>
                <v-row no-gutters class="mb-5">
                    <v-col cols="12">
                        <v-card>
                            <v-container class="px-3 py-0">
                                <v-row align="center">
                                    <v-col>
                                        <v-menu
                                            v-model="startDateDialog"
                                            :close-on-content-click="false"
                                            max-width="290"
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    label="Start Date"
                                                    readonly
                                                    v-model="formattedStartDate"
                                                    name="received date"
                                                    v-on="on"
                                                    append-icon="event"
                                                    hide-details
                                                    @click:append="startDateDialog = true"
                                                    :clearable="false"
                                                ></v-text-field>
                                            </template>
                                            <v-date-picker
                                                no-title
                                                v-model="searchParams.from"
                                                @change="startDateDialog = false"
                                            ></v-date-picker>
                                        </v-menu>
                                    </v-col>
                                    <v-col>
                                        <v-menu
                                            v-model="endDateDialog"
                                            :close-on-content-click="false"
                                            max-width="290"
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="formattedEndDate"
                                                    label="End Date"
                                                    readonly
                                                    name="end date"
                                                    v-on="on"
                                                    append-icon="event"
                                                    hide-details
                                                    @click:append="endDateDialog = true"
                                                    :clearable="false"
                                                ></v-text-field>
                                            </template>
                                            <v-date-picker
                                                no-title
                                                v-model="searchParams.to"
                                                @change="endDateDialog = false"
                                            ></v-date-picker>
                                        </v-menu>
                                    </v-col>
                                    <v-col class="shrink">
                                        <v-btn text icon @click.native="performSearch">
                                            <v-icon>mdi-magnify</v-icon>
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row class="mx-0 pb-3">
                    <v-spacer></v-spacer>
                    <v-menu offset-y>
                        <template v-slot:activator="{ on }">
                            <v-btn small tile v-on="on">
                                <v-icon>save_alt</v-icon>&nbsp;Export
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item @click="exportCsv()">
                                <v-list-item-title>
                                    <v-icon>grid_on</v-icon>&nbsp;Excel (CSV)
                                    <v-chip x-small>limit {{maxRowsPerCsv}} rows</v-chip>
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-row>
                <v-card fill-height>
                    <v-data-table
                        :items="items"
                        :items-per-page="options.itemsPerPage"
                        :headers="headers"
                        :loading="$wait.is(['loading unit metrics'])"
                        :footer-props.sync="footerProps"
                        :options.sync="options"
                        :server-items-length="serverCount"
                        :multi-sort="false"
                        :dense="true"
                        :height="420"
                    >
                        <template v-slot:item="items">
                            <tr>
                                <td>{{items.item.date | formatDate('DD MMM YYYY')}}</td>
                                <td>{{items.item.runNumber}}</td>
                                <td>{{items.item.hours}}</td>
                                <td>{{items.item.kilometres}}</td>
                                <td>{{items.item.endingHours}}</td>
                                <td>{{items.item.endingKilometres}}</td>
                            </tr>
                        </template>
                        <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
                        <template v-slot:no-results>
                            <v-layout row align-center>
                                <v-flex>No matching records found</v-flex>
                            </v-layout>
                        </template>
                    </v-data-table>
                </v-card>
            </v-container>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="close">Cancel</v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>

<script>
  import moment from 'moment';
  import {DateFormat} from '@/plugins/date-filter';
  import UnitMetricsClient from '@/components/Units/Details/UnitMetrics/UnitMetricsClient';
  import DownloadHelper from '@/lib/DownloadHelper';

  export default {
    mixins: [UnitMetricsClient],
    props: {
      value: {
        required: true
      }
    },
    data() {
      return {
        maxRowsPerCsv: 1000,
        items: [],
        serverCount: 0,
        unitId: null,
        startDateDialog: false,
        endDateDialog: false,
        options: {
          itemsPerPage: 20,
          sortBy: ['date'],
          sortDesc: [true]
        },
        footerProps: {
          itemsPerPageOptions: [20, 100, 200]
        },
        headers: [
          {text: 'Date', value: 'date'},
          {text: 'Run #', value: 'runNumber', sortable: false},
          {text: 'Hours', value: 'hours', sortable: false},
          {text: 'Kilometres', value: 'kilometres', sortable: false},
          {text: 'End Hrs', value: 'endingHours', sortable: false},
          {text: 'End Kms', value: 'endingKilometres', sortable: false},
        ],
        searchParams: {
          from: null,
          to: null
        },
      };
    },

    computed: {
      formTitle() {
        return 'Unit Metrics for ' + this.value.unitName;
      },
      formattedStartDate: {
        get() {
          return this.searchParams.from ? moment(this.searchParams.from).format(DateFormat.DefaultDisplay) : '';
        },
        set() {
          //set when clearing
          this.searchParams.from = null;
        }
      },
      formattedEndDate: {
        get() {
          return this.searchParams.to ? moment(this.searchParams.to).format(DateFormat.DefaultDisplay) : '';
        },
        set() {
          //set when clearing
          this.searchParams.to = null;
        }
      },
    },
    methods: {
      close() {
        this.$emit('close');
      },
      async performSearch() {
        this.$wait.start('loading unit metrics');

        let results = await this.searchUnitMetrics(this.value.unitId, this.getSearchParams());
        this.items = results ? results.data : [];
        this.serverCount = results ? results.total : 0;

        this.$wait.end('loading unit metrics');
      },
      getSearchParams() {
        const {sortBy, sortDesc, page, itemsPerPage} = this.options;

        return {
          ...this.searchParams,
          page,
          pageSize: itemsPerPage,
          orderBy: sortBy.length ? sortBy[0] : null,
          orderByDescending: sortDesc.length ? sortDesc[0] : false
        };
      },
      init() {
        this.items = [];
        this.serverCount = 0;
        this.searchParams = {
          from: moment().add(-2, 'month').format(DateFormat.Canonical),
          to: moment().format(DateFormat.Canonical),
        };
      },
      async exportCsv() {
        let searchParams = this.getSearchParams();
        searchParams.page = 1;
        searchParams.pageSize = this.maxRowsPerCsv;

        const results = await this.searchUnitMetrics(this.value.unitId, searchParams);
        const data = results ? results.data : [];
        const rows = data.map(x => [moment(x.date).format(DateFormat.DefaultDisplay), x.runNumber, x.hours, x.kilometres, x.endingHours, x.endingKilometres]);
        const csv = DownloadHelper.makeCsv(['Date', 'Run #', 'Hours', 'Kilometres', 'End Hrs', 'End Kms'], rows);
        DownloadHelper.download(`${this.value.unitName}_UnitMetrics_${moment().format(DateFormat.Canonical)}.csv`, new Blob([csv]));
      }
    },
    watch: {
      options: {
        async handler() {
          await this.performSearch();
        },
        deep: true
      }
    },
    async mounted() {
      this.init();
      await this.performSearch();
    }
  };
</script>

<style>
    .centered-input input {
        text-align: center;
    }
</style>
